import _ from "lodash";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { DateTime } from "luxon";
import { GlobalModalsContext, brand } from "../../../context";
import { useQuery } from "@tanstack/react-query";
import { getUserPoints } from "../../../api/points/index.js";
import { Loader } from "../../../components/widgets/Loader/index.jsx";
import * as yup from "yup";
import { gaussianRounding, useProfileState } from "../../../umbilical_hocs";

const entrySchema = yup.object().shape({
  description: yup.string().required(),
  type: yup.string().required().oneOf(["CR", "DB"]),
  amount: yup.number().required(),
  timestamp: yup.string().required(),
});

export const PointsLedger = ({
  user_uid,
  size = "small",
  hideInMobile,
  currency = "TRV",
}) => {
  const { brand } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);

  const { data, isLoading } = useQuery({
    queryKey: ["points", user_uid, currency],
    queryFn: () => getUserPoints({ guest_crm_uid: user_uid, currency }),
    refetchOnWindowFocus: false,
  });

  const rawLedgerEntries = data?.data?.results ?? [];
  const ledgerEntries = rawLedgerEntries.map((entry) => {
    return entrySchema.cast({
      description: entry.description,
      type: entry.entry_type,
      amount: parseFloat(entry.amount),
      timestamp: entry.created,
    });
  });

  const sortedEntries = _.reverse(
    _.sortBy(ledgerEntries, (v) => DateTime.fromISO(v.timestamp).toMillis())
  );

  const currentBalance = sortedEntries.reduce(
    (acc, curr) => acc + curr.amount,
    0
  );

  return (
    <div
      className="PointsLedger"
      data-brand={brand}
      data-hideInMobile={hideInMobile ? "true" : "false"}
      data-size={size}>
      {isLoading && <Loader />}
      <h5 className="PointsLedger__header" data-brand={brand}>
        {currency !== "TRV"
          ? `${currency} Ledger`
          : `Travily ${t("pointsHistoryTable")} `}
        <span>
          {currency !== "TRV"
            ? currentBalance.toLocaleString("en-US", {
                style: "currency",
                currency,
                maximumFractionDigits: 2,
              })
            : currentBalance.toLocaleString("en-US", {
                maximumFractionDigits: 2,
              })}{" "}
          {currency !== "TRV" ? null : "Travily Points"}
        </span>
      </h5>
      <table className="PointsLedger__table" data-brand={brand}>
        <thead>
          <tr>
            <th data-brand={brand}>#</th>
            <th data-brand={brand}>{t("description")}</th>
            <th data-brand={brand}>{t("pointsIn")}</th>
            <th data-brand={brand}>{t("pointsOut")}</th>
            <th data-brand={brand} data-hideinmobile="true">
              {t("balance")}
            </th>
            <th data-brand={brand} data-hideinmobile="true">
              {t("created")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedEntries.map((item, index) => {
            const sumUpToIndex = gaussianRounding(
              sortedEntries.reduce((acc, curr, i) => {
                if (i >= index) {
                  return acc + curr.amount;
                }
                return acc;
              }, 0)
            );

            const amount = Math.abs(item.amount);

            return (
              <tr key={index}>
                <td data-brand={brand}>{index + 1}</td>
                <td data-brand={brand}>{item.description}</td>
                <td data-brand={brand}>
                  {item.type === "CR" ? `+ ${amount}` : ""}
                </td>
                <td data-brand={brand}>
                  {item.type === "DB" ? `- ${amount}` : ""}
                </td>
                <td data-brand={brand} data-hideinmobile="true">
                  {sumUpToIndex}
                </td>
                <td data-brand={brand} data-hideinmobile="true">
                  {DateTime.fromISO(item.timestamp).toLocaleString(
                    DateTime.DATETIME_MED_WITH_WEEKDAY
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
PointsLedger.defaultProps = { size: "small", currency: "TRV" };
PointsLedger.propTypes = {
  user_uid: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["small", "large"]),
  hideInMobile: PropTypes.bool.isRequired,
  currency: PropTypes.string.isRequired,
};

const MyProfilePoints = () => {
  const { setShowTopUpPointsModal } = useContext(GlobalModalsContext);
  const { t } = useContext(TranslationContext);
  const { profile } = useProfileState();
  const [activeTab, setActiveTab] = useState("points");

  return (
    <div className="MyProfilePoints" data-brand={brand}>
      <div className="MyProfilePoints__actions">
        {/* <button */}
        {/*   className="Button MyProfilePoints__top-up-btn" */}
        {/*   data-brand={brand} */}
        {/*   data-ghost={activeTab !== "points"} */}
        {/*   onClick={() => setActiveTab("points")}> */}
        {/*   Points Ledger */}
        {/* </button> */}
        {/* <button */}
        {/*   className="Button" */}
        {/*   data-brand={brand} */}
        {/*   data-ghost={activeTab !== "cash"} */}
        {/*   onClick={() => setActiveTab("cash")}> */}
        {/*   Cash Ledger */}
        {/* </button> */}
        {activeTab === "points" && (
          <button
            className="Button MyProfilePoints__top-up-btn"
            data-brand={brand}
            onClick={() => setShowTopUpPointsModal(true)}>
            {t("topUp")}
          </button>
        )}
      </div>
      <PointsLedger
        user_uid={profile?.uuid}
        size="large"
        currency={activeTab === "points" ? "TRV" : "USD"}
      />
      {brand === "travily" && (
        <span>
          If you have questions about your points prior to the 25th January
          2024, please contact us.
        </span>
      )}
    </div>
  );
};
MyProfilePoints.propTypes = { profile: PropTypes.object.isRequired };

export default MyProfilePoints;
